"use client";

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";
import { useConfirmDialogStore } from "@erp/shared-libs/stores";
import { cn } from "@erp/shared-libs/utils";
import { isUndefined } from "lodash-es";
import { AlertCircle } from "lucide-react";
import { ReactNode, useCallback, useState } from "react";

export interface ConfirmDialogProps {
  title: ReactNode;
  content: ReactNode;
  open: boolean;
  onConfirm: () => boolean | void | Promise<boolean | void>;
  onCancel?: () => void | Promise<void>;
}

export const ConfirmDialogProvider = () => {
  // ==================================================================
  // Hooks
  // ==================================================================
  const {
    title,
    content,
    open,
    contentClassName,
    continueOnly,
    confirmText,
    onConfirm,
    onCancel,
    closeDialog,
  } = useConfirmDialogStore();
  const [isConfirming, setIsConfirming] = useState(false);

  // ==================================================================
  // Functions
  // ==================================================================
  /**
   * 關閉對話框
   */
  const handleClose = useCallback(async () => {
    await onCancel?.();
    closeDialog();
  }, [onCancel, closeDialog]);

  /**
   * 確認操作
   */
  const handleConfirm = useCallback(async () => {
    setIsConfirming(true);

    try {
      const isSuccess = await onConfirm();

      if (!isUndefined(isSuccess) && !isSuccess) {
        return;
      }

      closeDialog();
    } finally {
      // 避免重複點擊導致的多次確認
      // 800毫秒後允許再次確認
      setTimeout(() => {
        setIsConfirming(false);
      }, 800);
    }
  }, [closeDialog, onConfirm]);

  /**
   * 處理對話框開啟狀態變更
   * Escape 等關閉途徑語義上等同取消,必須走 handleClose 觸發 onCancel,
   * 否則以 Promise 包裝 openDialog 的呼叫端會永遠 pending(isSubmitting 卡死)
   */
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        void handleClose();
      }
    },
    [handleClose]
  );

  // ==================================================================
  // Render
  // ==================================================================
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        hideClose
        className={cn(
          // 視窗有最大高度：內容太長時只捲動內容區，標題與「取消／確認」固定不動
          "bg-primary-50 flex max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] flex-col gap-0 p-0 sm:max-w-sm",
          contentClassName
        )}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div
          className="bg-on-background border-primary-200 shadow-primary/10 flex min-h-0 flex-1 flex-col rounded-b-2xl
            rounded-t-xl border-b shadow-md"
        >
          <div className="shrink-0 px-4 pt-6">
            <div className="bg-primary-50 border-primary-200 flex items-center gap-2 rounded-full border px-4 py-2">
              <AlertCircle className="text-primary" size={20} />

              <DialogTitle asChild>
                <div className="text-primary-900 text-sm font-bold">{title}</div>
              </DialogTitle>
            </div>
          </div>

          <div className="text-primary-900 min-h-0 overflow-y-auto whitespace-pre-wrap px-6 pb-12 pt-10 text-sm">
            {content}
          </div>
        </div>

        <div className="flex shrink-0 items-end justify-end gap-4 px-4 py-2.5">
          {!continueOnly && (
            <Button size="sm" className="px-8" variant="secondary" onClick={handleClose}>
              取消
            </Button>
          )}
          <Button size="sm" className="px-8" disabled={isConfirming} onClick={handleConfirm}>
            {confirmText ?? (continueOnly ? "繼續" : "確認")}
          </Button>
        </div>
      </DialogContent>
      <DialogDescription />
    </Dialog>
  );
};
